import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { HiOutlineMapPin, HiOutlinePhoto, HiOutlineInformationCircle, HiStar } from "react-icons/hi2";
import { FaBed, FaCheckCircle, FaUserEdit } from "react-icons/fa";
import { baseurl } from "../../baseurl";

const HotelDetail = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // States
  const [hotelData, setHotelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // --- MARKUP STATE ---
  const [hotelMarkup, setHotelMarkup] = useState({ type: "flat", amount: 0 });

  // Guest configuration from URL
  const adultsCount = parseInt(searchParams.get("adult") || "1");
  const childrenCount = parseInt(searchParams.get("children") || "0");
  const totalGuests = adultsCount + childrenCount;

  const [bookingDetails, setBookingDetails] = useState({
    email: "",
    phoneNumber: "",
    guests: Array.from({ length: totalGuests }, (_, i) => ({
      type: i < adultsCount ? "Adult" : "Child",
      title: "Mr",
      firstName: "",
      lastName: "",
      age: i < adultsCount ? "25" : "10",
      isLead: i === 0
    }))
  });

  // --- MARKUP CALCULATION LOGIC ---
  const calculateFinalFare = useCallback((baseFare) => {
    const fare = parseFloat(baseFare) || 0;
    const markupAmt = parseFloat(hotelMarkup.amount) || 0;
    const type = hotelMarkup.type?.toLowerCase(); 
    
    let finalFare = 0;
    if (type === "percent" || type === "percentage") {
      finalFare = fare + (fare * (markupAmt / 100));
    } else {
      finalFare = fare + markupAmt;
    }
    return Math.round(finalFare);
  }, [hotelMarkup]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // 1. Fetch Markup Configuration
        try {
          const markupRes = await axios.post(`${baseurl}/getServiceMarkupsGuest`);
          const markupData = markupRes.data?.markups || [];
          if (Array.isArray(markupData)) {
            const hotelConfig = markupData.find(m => String(m.service_id) === "1");
            if (hotelConfig) {
              setHotelMarkup({
                type: hotelConfig.markup_type,
                amount: hotelConfig.amount
              });
            }
          }
        } catch (mErr) {
          console.error("Error fetching markup:", mErr);
        }

        // 2. Fetch Hotel Details
        const response = await axios.post(`${baseurl}/hotelDetailsGuest`, { hotelToken: token });
        if (response.data.status === "success") {
          setHotelData(response.data);
        } else {
          setError("Failed to fetch hotel details.");
        }
      } catch (err) {
        setError("An error occurred while loading hotel details.");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchInitialData();
  }, [token]);

  const handleGuestChange = (index, field, value) => {
    const updatedGuests = [...bookingDetails.guests];
    updatedGuests[index][field] = value;
    setBookingDetails({ ...bookingDetails, guests: updatedGuests });
  };

  const handleConfirmBooking = () => {
    if (!bookingDetails.email || !bookingDetails.phoneNumber) {
      alert("Please fill in contact details");
      return;
    }

    // Attach calculated price to the selected room for the commit page
    const roomWithMarkup = {
      ...selectedRoom,
      Price: {
        ...selectedRoom.Price,
        PublishedPriceWithMarkup: calculateFinalFare(selectedRoom.Price.PublishedPriceRoundedOff)
      }
    };

    const payload = {
      hotelInfo: hotelData.hotelDetails.HotelDetails.HotelInfoResult.HotelDetails,
      roomDetails: roomWithMarkup,
      guestDetails: bookingDetails,
      token: token,
      markupApplied: hotelMarkup
    };

    navigate("/hotel-commit", { state: payload });
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center vh-100"><div className="spinner-grow text-primary"></div></div>;
  if (error || !hotelData) return <div className="container text-center " style={{marginTop: "100px"}}><div className="alert alert-danger">{error || "Hotel not found"}</div></div>;

  const details = hotelData.hotelDetails.HotelDetails.HotelInfoResult.HotelDetails;
  const rooms = hotelData.roomList.RoomList.GetHotelRoomResult.HotelRoomsDetails;

  return (
    <section className="bg-light min-vh-100 py-4">
      <div className="container" style={{marginTop: "100px"}}>
        <div className="card border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
          <div className="row g-0">
            <div className="col-md-5">
              <img src={details.Images[0]} alt={details.HotelName} className="w-100 h-100" style={{ objectFit: "cover", minHeight: "350px" }} />
            </div>
            <div className="col-md-7 p-4">
              <div className="d-flex justify-content-between">
                <h2 className="fw-bold">{details.HotelName}</h2>
                <div className="text-warning"><HiStar /> {details.StarRating} Star</div>
              </div>
              <p className="text-muted small"><HiOutlineMapPin /> {details.Address}</p>
              
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                  <div className="p-2 bg-light rounded text-center">
                    <small className="text-muted d-block">Check-In</small>
                    <span className="fw-bold">{details.checkInTime}</span>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="p-2 bg-light rounded text-center">
                    <small className="text-muted d-block">Check-Out</small>
                    <span className="fw-bold">{details.checkOutTime}</span>
                  </div>
                </div>
              </div>

              <h6 className="fw-bold text-primary small text-uppercase">Description</h6>
             <p 
  className="small text-muted mb-0" 
  
  dangerouslySetInnerHTML={{ __html: details.Description }} 
/> 
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-8">
            {!selectedRoom ? (
              <>
                <h5 className="fw-bold mb-3"><FaBed /> Available Room Types</h5>
                {rooms.map((room, index) => {
                  const finalRoomPrice = calculateFinalFare(room.Price.PublishedPriceRoundedOff);
                  return (
                    <div key={index} className="card border-0 shadow-sm mb-3 rounded-4 overflow-hidden">
                      <div className="card-body p-4">
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <h5 className="fw-bold mb-1">{room.RoomTypeName}</h5>
                            <span className="badge bg-success-subtle text-success mb-2 px-2"><FaCheckCircle /> {room.room_only || "Standard Room"}</span>
                            <p className="x-small text-muted mb-0">{room.CancellationPolicy}</p>
                          </div>
                          <div className="col-md-4 text-md-end mt-3 mt-md-0">
                            <div className="text-muted small">Total Price (Inc. Markup)</div>
                            <div className="h3 fw-bold text-primary mb-2">₹{finalRoomPrice}</div>
                            <button onClick={() => setSelectedRoom(room)} className="btn btn-primary w-100 rounded-pill fw-bold">SELECT ROOM</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="card border-0 shadow-sm rounded-4 p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0"><FaUserEdit className="text-primary me-2"/> Guest Details</h5>
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setSelectedRoom(null)}>Change Room</button>
                </div>

                <div className="row g-3 mb-4 bg-primary-subtle p-3 rounded-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Email</label>
                    <input type="email" className="form-control" placeholder="Enter email" value={bookingDetails.email} onChange={(e) => setBookingDetails({...bookingDetails, email: e.target.value})} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Mobile</label>
                    <input type="tel" className="form-control" placeholder="Enter phone number" value={bookingDetails.phoneNumber} onChange={(e) => setBookingDetails({...bookingDetails, phoneNumber: e.target.value})} />
                  </div>
                </div>

                {bookingDetails.guests.map((guest, idx) => (
                  <div key={idx} className="mb-4 border-bottom pb-4">
                    <h6 className="fw-bold small text-muted mb-3">
                      {guest.isLead ? "LEAD GUEST (ADULT)" : `${guest.type.toUpperCase()} ${idx + 1}`}
                    </h6>
                    <div className="row g-2">
                      <div className="col-md-2">
                        <select className="form-select" value={guest.title} onChange={(e) => handleGuestChange(idx, "title", e.target.value)}>
                          <option>Mr</option><option>Mrs</option><option>Ms</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <input type="text" className="form-control" placeholder="First Name" onChange={(e) => handleGuestChange(idx, "firstName", e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <input type="text" className="form-control" placeholder="Last Name" onChange={(e) => handleGuestChange(idx, "lastName", e.target.value)} />
                      </div>
                      <div className="col-md-2">
                        <input type="number" className="form-control" placeholder="Age" value={guest.age} onChange={(e) => handleGuestChange(idx, "age", e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={handleConfirmBooking} className="btn btn-primary btn-lg w-100 rounded-pill fw-bold">PROCEED TO PAY</button>
              </div>
            )}
          </div>

          <div className="col-lg-4">
            <div className="sticky-top" style={{ top: "75px", borderRadius: "5px", }}>
              <div className="bg-dark text-white p-4 rounded-4 shadow-sm mb-4">
                <h6 className="fw-bold text-info small text-uppercase mb-3">Fare Details</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span className="opacity-75">Price (Inc. Markup)</span>
                  <span>₹{calculateFinalFare(selectedRoom ? selectedRoom.Price.PublishedPriceRoundedOff : rooms[0].Price.PublishedPriceRoundedOff)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="opacity-75">Tax & Fees</span>
                  <span className="text-success">INCLUDED</span>
                </div>
                <hr className="opacity-25" />
                <div className="d-flex justify-content-between h4 fw-bold">
                  <span>Total Payable</span>
                  <span className="text-info">₹{calculateFinalFare(selectedRoom ? selectedRoom.Price.PublishedPriceRoundedOff : rooms[0].Price.PublishedPriceRoundedOff)}</span>
                </div>
              </div>

              <div className="bg-white p-4 rounded-4 shadow-sm">
                <h6 className="fw-bold small mb-3 text-uppercase"><HiOutlineInformationCircle /> Hotel Policies</h6>
                <div className="small text-muted" style={{ maxHeight: "200px", overflowY: "auto", fontSize: "11px" }}>
                  {details.HotelPolicy ? details.HotelPolicy.split(',').map((p, i) => (
                    <div key={i} className="mb-2">• {p.trim()}</div>
                  )) : "Standard hotel policies apply."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HotelDetail;